/* Iter 418.ap (Dirk, 2026-02-28) — Interactive AI chat on the plant
   report. Sits above the parameter table. CRM-only (server-enforced).
   Users can type any plain-English question about the plant and
   Claude answers using the same dossier the AI Insights PDF uses.
*/
import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, User, MessageSquareText, RefreshCw } from "lucide-react";
import api, { formatErr } from "../../lib/api";

const SUGGESTIONS = [
  "What are the top 3 things a plant manager should worry about right now?",
  "Which parameters are trending in the wrong direction?",
  "Compare uptime this window vs the previous equivalent window",
  "Is our water recovery rate improving or declining?",
  "What sensors look like they've drifted or failed?",
];

export default function AIChatPanel({ customerId, customerName, fromIso, toIso, allTime }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(`plant-chat-${customerId}-${Date.now()}`);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // Reset the thread when the customer or date range changes so
  // Claude doesn't keep referencing the old dossier.
  useEffect(() => {
    setMessages([]);
    sessionId.current = `plant-chat-${customerId}-${Date.now()}`;
  }, [customerId, fromIso, toIso, allTime]);

  const ask = async (question) => {
    const q = (question || input).trim();
    if (!q || busy) return;
    setBusy(true);
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q, at: new Date().toISOString() }]);
    try {
      const params = allTime ? { all_time: 1 } : { from: fromIso, to: toIso };
      const { data } = await api.post(
        `/perf/plant-report/${customerId}/ai-chat`,
        { question: q, session_id: sessionId.current },
        { params, timeout: 90_000 },
      );
      setMessages((m) => [...m, { role: "ai", text: data.answer, model: data.model, at: data.generated_at }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: formatErr(e), error: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="hv-card p-3 space-y-2 border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.03] to-emerald-500/[0.03]"
      data-testid="plant-report-ai-chat"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-300" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold">Ask AI about this plant</div>
            <div className="text-[10px] text-[#647087]">
              CRM-only · Claude Sonnet · answers grounded in this window&rsquo;s dossier
            </div>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => { setMessages([]); sessionId.current = `plant-chat-${customerId}-${Date.now()}`; }}
            className="hv-btn hv-btn-ghost text-[10px]"
            data-testid="ai-chat-reset"
          >
            <RefreshCw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      {messages.length === 0 && (
        <div className="space-y-1.5" data-testid="ai-chat-suggestions">
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">Try one of these:</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => ask(s)}
                disabled={busy}
                className="text-left text-[11px] px-2 py-1.5 rounded border border-white/5 bg-white/[0.02] hover:border-cyan-500/40 hover:bg-cyan-500/[0.04] transition disabled:opacity-40"
                data-testid={`ai-chat-suggestion-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {messages.length > 0 && (
        <div
          ref={scrollRef}
          className="max-h-[420px] overflow-y-auto space-y-2 p-2 border border-white/5 rounded bg-black/20"
          data-testid="ai-chat-history"
        >
          {messages.map((m, i) => (
            <div key={i} className="flex gap-2" data-testid={`ai-chat-msg-${i}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-emerald-500/20 text-emerald-300" : "bg-cyan-500/20 text-cyan-300"}`}>
                {m.role === "user" ? <User className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[9px] uppercase tracking-wider ${m.role === "user" ? "text-emerald-300" : "text-cyan-300"}`}>
                  {m.role === "user" ? "You" : (m.model || "Claude")}
                </div>
                <div className={`text-[12px] whitespace-pre-wrap leading-relaxed ${m.error ? "text-rose-300" : "text-[#e2e8f0]"}`}>
                  {m.text}
                </div>
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex gap-2 items-center text-[11px] text-cyan-300" data-testid="ai-chat-busy">
              <MessageSquareText className="w-3 h-3 animate-pulse" />
              Claude is reading the dossier…
            </div>
          )}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); ask(); }}
        className="flex gap-2"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Ask about ${customerName || "this plant"}…`}
          className="hv-input flex-1 text-xs"
          disabled={busy}
          data-testid="ai-chat-input"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="ai-chat-send"
        >
          <Send className="w-3 h-3" /> {busy ? "…" : "Ask"}
        </button>
      </form>
    </div>
  );
}
